import { Box, Button, Heading, Icon, IconButton, Text, TextButton } from '@zillow/constellation-10';
import { styled } from '@/styled-system/jsx';
import { useCallback, useEffect, useState } from 'react';
import { IconChevronDownFilled, IconCloseFilled } from '@zillow/constellation-icons';
import { MainLayoutType, useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { ExportDialog } from '../ExportDialog';
import type { FileType, GraphConfig, SelectedGraphs } from '../ExportDialog';
import { MarketDropdown } from './MarketDropdown';

const CustomerAnalyticsPageContent = styled(Box, {
  base: {
    paddingInline: '800',
    paddingBlock: '900',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: 'fit-content',
    backgroundColor: 'gray.50',
    position: 'absolute',
    xxl: { width: 'auto', height: 'auto', position: 'relative' },
  },
  variants: {
    addSidebarSpacing: {
      true: {
        xxl: {
          maxWidth: 'calc(100% - 408px)',
        },
      },
    },
  },
});

const SidebarContentWrapper = styled(Box, {
  base: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: '0',
    borderLeft: '1px solid',
    borderColor: 'border.muted !important',
    width: '408px',
    maxWidth: '408px',
    minWidth: '408px',
    height: '100vh',
    backgroundColor: 'white',
    transition: 'right 0.3s ease-in-out',
    zIndex: 1001,
    overflowY: 'auto',
    xxl: {
      zIndex: 0,
      top: '0',
      right: '0',
      paddingBlockStart: 'var(--header-height, 217px)',
    },
    xxlDown: {
      paddingBlockStart: '0 !important',
    },
  },
  variants: {
    open: {
      true: {
        right: '0',
      },
      false: {
        right: `-${'408px'}`,
      },
    },
  },
});

const SidebarBackdrop = styled(Box, {
  base: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    zIndex: 1000,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    transition: 'opacity 0.3s ease-in-out',
    xxl: {
      display: 'none',
    },
  },
  variants: {
    open: {
      true: {
        display: 'block',
      },
      false: {
        display: 'none',
      },
    },
  },
});

const CustomerAnalyticsPageSidebar = ({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <>
      <SidebarContentWrapper open={open}>
        {title && (
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '400',
              borderBottom: '1px solid',
              borderColor: 'border.muted !important',
              xxl: {
                border: 'none',
              },
            }}
          >
            <Heading level={5}>{title}</Heading>
            <IconButton
              onClick={onClose}
              css={{
                width: '24px',
                height: '24px',
                _hover: { backgroundColor: 'transparent !important' },
                xxl: { display: 'none' },
              }}
              title="Close"
              emphasis="bare"
              shape="square"
              size="md"
            >
              <Icon fill="black">
                <IconCloseFilled />
              </Icon>
            </IconButton>
          </Box>
        )}
        <Box css={{ flexGrow: 1 }}>{children}</Box>
      </SidebarContentWrapper>
      <SidebarBackdrop open={open} onClick={onClose} />
    </>
  );
};

type CustomerAnalyticsPageProps = {
  children: React.ReactNode;
  header: React.ReactNode;
  subheader?: string;
  sidebar?: React.ReactNode;
  sidebarTitle?: string;
  showSidebarText?: string;
  tabName?: 'Overview' | 'Ad Spend' | 'Market Data' | 'Renter Data';
  graphConfigs?: Array<GraphConfig>;
  onExport?: (fileType: FileType, selectedGraphs: SelectedGraphs) => Promise<void>;
  hideMarketSelect?: boolean;
  marketSelectLabel?: string;
};

export const CustomerAnalyticsPage = ({
  children,
  header,
  subheader,
  sidebar,
  sidebarTitle,
  showSidebarText = 'Show Sidebar',
  tabName,
  graphConfigs,
  onExport,
  hideMarketSelect = false,
  marketSelectLabel = 'Comparison market',
}: CustomerAnalyticsPageProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const { layout } = useMainLayout();

  const haveSidebar = !!sidebar;
  const showExportButton = !!tabName && !!graphConfigs && !!onExport;
  const isMap = layout === MainLayoutType.MAP;

  return (
    <Box css={{ display: 'flex', flexGrow: 1, position: 'relative', width: '100%' }}>
      <CustomerAnalyticsPageContent addSidebarSpacing={haveSidebar}>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '800' }}>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <Heading level={4}>{header}</Heading>
            {subheader && <Text>{subheader}</Text>}
          </Box>
          <Box css={{ display: 'none', xxl: { display: 'flex', alignItems: 'center' } }}>
            {!isMap && <MarketDropdown hideMarketSelect={hideMarketSelect} labelText={marketSelectLabel} />}
            {showExportButton && (
              <Button
                css={{ marginLeft: '200' }}
                tone="neutral"
                icon={<IconChevronDownFilled />}
                iconPosition="end"
                onClick={() => setExportDialogOpen(true)}
              >
                Export
              </Button>
            )}
          </Box>
          {haveSidebar && (
            <TextButton
              css={{ display: 'inline', xxl: { display: 'none' } }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {showSidebarText}
            </TextButton>
          )}
        </Box>
        {children}
      </CustomerAnalyticsPageContent>
      {sidebar && (
        <CustomerAnalyticsPageSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} title={sidebarTitle}>
          {sidebar}
        </CustomerAnalyticsPageSidebar>
      )}
      {showExportButton && tabName && graphConfigs && onExport && (
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          tabName={tabName}
          graphs={graphConfigs}
          onExport={onExport}
        />
      )}
    </Box>
  );
};
